import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';

import { TeamApprovalStatus } from '@/components/constants/enums';
import TeamApprovalStatusIcon from '@/components/molecules/TeamApprovalStatusIcon';
import { ScheduleMatchup } from '@/entities';
import { useOrganizationStore } from '@/store/organization-store';

interface ScheduleCardProps {
	id: string;
	time: string;
	timeBetweenMatches?: string | null;
	firstTeam: string;
	secondTeam: string;
	venue?: string;
	firstTeamStatus?: TeamApprovalStatus;
	secondTeamStatus?: TeamApprovalStatus;
	status?: 'newTime' | 'newLocation';
	isWeekView?: boolean;
	onClickProposedNew?: (id: string) => void;
	isFinalSchedule?: boolean;
	isPendingSchedule?: boolean;
	isCompleted?: boolean;
	onClickApprove?: (id: string) => void;
	matchup?: ScheduleMatchup;
}

// phase 2
// const getMatchupStatus = (matchup: ScheduleMatchup) => {
// 	const now = dayjs();
// 	const plannedStart = dayjs(matchup.matchPlannedStart);
// 	const matchStart = matchup.matchStart ? dayjs(matchup.matchStart) : null;
// 	const matchCompleted = matchup.matchCompleted ? dayjs(matchup.matchCompleted) : null;

// 	if (matchStart && !matchCompleted) {
// 		return { status: 'Live', color: 'red', text: 'Live', showScores: true };
// 	}

// 	if (!matchStart && plannedStart.isAfter(now) && plannedStart.diff(now, 'minutes') <= 30) {
// 		return { status: 'Starting Soon', color: 'blue', text: 'Starting soon', showScores: false };
// 	}

// 	if (matchCompleted) {
// 		return { status: 'View Scores', color: 'gray', text: 'View scores', showScores: true };
// 	}

// 	return { statusMatch: 'Scheduled', color: 'gray', text: '', showScores: false };
// };

export const ScheduleCard = ({
	time,
	timeBetweenMatches,
	firstTeam,
	secondTeam,
	venue,
	status,
	isWeekView,
	firstTeamStatus,
	secondTeamStatus,
	isFinalSchedule,
	isPendingSchedule,
	isCompleted,
	id
}: ScheduleCardProps) => {
	const { teamLeagueSlug } = useOrganizationStore();
	const isFinalsMatchCompleted = isCompleted && isFinalSchedule;
	const teamsColor = isFinalsMatchCompleted ? 'text-gray-lighter' : 'text-blue-500';
	const locationAndTimeColor = isFinalsMatchCompleted ? 'text-muted-blue' : 'text-blue-400';
	const bgBorderCard = isFinalsMatchCompleted ? 'border-gray-200' : 'border-blue-200';
	const bgCard = isFinalsMatchCompleted ? 'bg-soft-white' : 'bg-light-blue';

	//phase 2
	//const { statusMatch, color, text, showScores } = getMatchupStatus(matchup!);
	const getHeader = () => {
		if (status === 'newTime') return 'New time!';
		if (status === 'newLocation') return 'New location!';
		return null;
	};

	const renderHeader = () =>
		getHeader() && (
			<div className="inline-block  rounded-lg border border-blue-200 px-2 text-xs font-medium text-blue-300  md:text-sm">{getHeader()}</div>
		);

	const renderTeams = () => {
		return (
			<div className={`flex flex-col justify-center pb-2  pt-0 text-xs font-semibold  ${teamsColor} sm:py-2 md:pt-0`}>
				<div className="flex items-center justify-between">
					<p className="truncate  md:overflow-visible  md:whitespace-normal">
						{firstTeam} <span className="inline-block  pl-1 sm:hidden">vs.</span>
					</p>

					{isPendingSchedule && firstTeamStatus ? <TeamApprovalStatusIcon status={firstTeamStatus} /> : null}
				</div>
				<p className="hidden py-1 md:flex">vs</p>
				<div className="flex items-center  justify-between  pb-2  sm:pb-2 sm:pr-0 md:pb-0">
					<p className="truncate pr-4 sm:pr-0 md:overflow-visible md:whitespace-normal">{secondTeam}</p>

					{isPendingSchedule && secondTeamStatus ? <TeamApprovalStatusIcon status={secondTeamStatus} /> : null}
				</div>

				{/* <div className="flex items-center gap-2">
					{text && <span className={`text- px-2 py-1 text-sm font-bold${color}-600`}>{text}</span>}
					{showScores && (
						<a href="#" className="text-blue-500">
							View Scores
						</a>
					)}
				</div> */}
			</div>
		);
	};

	const renderLocation = () => (
		<div className={`flex items-center pb-1 pt-2 ${locationAndTimeColor}  sm:hidden sm:items-start md:flex`}>
			<FontAwesomeIcon className=" h-5 w-3  sm:h-5 sm:w-4" icon={faLocationDot} />
			<span className="pl-2 pr-4 text-xs font-medium ">{venue}</span>
		</div>
	);

	// weeklyView card type

	if (isWeekView) {
		return (
			<Link href={`/team-leagues/${teamLeagueSlug}/matchup/${id}`}>
				<div
					className={`hidden w-[140px] ${isCompleted && isFinalSchedule ? 'rounded-t-lg' : 'rounded-lg'}  border ${bgBorderCard} ${bgCard}  md:block  ${
						isFinalSchedule ? 'p-1' : 'max-w-[136px] px-4 py-2 sm:px-1 sm:pt-1'
					}`}
				>
					<div className="block">
						{isPendingSchedule ? (
							<div className="inline-block rounded-lg border border-blue-200 px-2 text-xs font-medium text-blue-300  md:text-xs">
								{getHeader()}
							</div>
						) : null}
						<div
							className={`flex items-center justify-between text-sm ${locationAndTimeColor} sm:hidden md:flex ${
								isPendingSchedule ? 'py-2' : 'pb-2 pt-0'
							}`}
						>
							<span className={`text-[10px] font-medium ${locationAndTimeColor} `}>{time}</span>
							{timeBetweenMatches ? <span className="text-[10px] font-normal">{timeBetweenMatches}</span> : null}
						</div>
					</div>
					{renderTeams()}
					{venue ? renderLocation() : null}
				</div>
				{isFinalsMatchCompleted ? (
					<div className="hidden justify-center rounded-b-[8px] bg-gray-light text-xs text-dark-gray sm:flex">Complete</div>
				) : null}
			</Link>
		);
	}

	return (
		<Link href={`/team-leagues/${teamLeagueSlug}/matchup/${id}`}>
			<div
				className={`hidden items-center rounded-lg border border-blue-200  px-4 pt-2 text-sm shadow-sm md:flex  ${bgBorderCard} ${bgCard} ${
					isFinalSchedule ? 'justify-between pb-2' : 'pb-5 '
				}`}
			>
				<div className="flex flex-col">
					{isPendingSchedule ? <div className="flex items-center gap-4 pb-2">{renderHeader()}</div> : null}

					<div className={`flex text-sm font-semibold items-center ${teamsColor}`}>
						<div className="flex items-center gap-2">
							<span>{firstTeam}</span>

							{isPendingSchedule && firstTeamStatus ? <TeamApprovalStatusIcon status={firstTeamStatus} /> : null}
						</div>
						<div className={`px-4 text-xs ${teamsColor}`}>vs</div>
						<div className="flex items-center gap-2">
							<span>{secondTeam}</span>

							{isPendingSchedule && secondTeamStatus ? <TeamApprovalStatusIcon status={secondTeamStatus} /> : null}
						</div>
					</div>
					{venue ? (
						<div className={`flex items-center gap-1 pt-[14px] text-xs  ${locationAndTimeColor}`}>
							<FontAwesomeIcon icon={faLocationDot} className="size-4" />
							<span>{venue}</span>
						</div>
					) : null}
				</div>

				<div className={`flex flex-col items-center gap-[14px] pl-[66px] ${isFinalSchedule ? 'pt-0' : 'pt-7'}`}>
					<div className={`text-sm font-semibold  ${isFinalsMatchCompleted ? 'text-gray-lighter' : 'text-blue-600'}  `}>{time}</div>
					{timeBetweenMatches ? (
						<div className={`text-xs ${isFinalsMatchCompleted ? 'text-muted-blue' : 'text-blue-300'}`}>{`(${timeBetweenMatches})`}</div>
					) : null}
				</div>
			</div>
		</Link>
	);
};
